import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import useRetryLabOrder from 'src/feed/LabOrderEventItem/useRetryLabOrder'
import { LabOrderStatus, VideoCallCancelledBy } from 'src/graphql.generated'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import useOpenLabReport from 'src/labOrder/useOpenLabReport'
import { ActivityFacts } from '../../api/patientConversation'
import { formatEventTime } from '../../format'
import { advancedColors } from '../../theme'
import { useMarkLabResultsViewed } from '../../api/useMarkLabResultsViewed'
import { callHasEnded } from './activityFacts'
import { useDisabledInPreview } from '../panelPreviewContext'

type LabOrderFacts = Extract<ActivityFacts, { kind: 'labOrder' }>
type VideoCallFacts = Extract<ActivityFacts, { kind: 'videoCall' }>

const LabOrderBody = ({ facts }: { facts: LabOrderFacts }) => {
  const [openReport, openReportStatus] = useOpenLabReport()
  const [retryLabOrder, retryLabOrderStatus] = useRetryLabOrder()
  const openDisabled = useDisabledInPreview(openReportStatus.loading)
  const retryDisabled = useDisabledInPreview(retryLabOrderStatus.loading)
  useMarkLabResultsViewed(facts.status === LabOrderStatus.Completed ? facts.orderID : null)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
          Order ID
        </Typography>
        <Typography variant="body2">{facts.orderID}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
          Events
        </Typography>
        {facts.events.map((event) => (
          <Typography variant="body2" key={`${event.status}-${event.occurredAt}`}>
            <span>{event.status}</span> {formatEventTime(event.occurredAt)}
          </Typography>
        ))}
        {facts.status === LabOrderStatus.Completed && (
          <Button
            disabled={openDisabled}
            onClick={() => {
              openReport(facts.orderID).catch(() => undefined)
            }}
          >
            Open results
          </Button>
        )}
        {facts.status === LabOrderStatus.Failed && (
          <Button
            disabled={retryDisabled}
            onClick={() => {
              retryLabOrder(facts.orderID).catch(() => undefined)
            }}
          >
            Retry
          </Button>
        )}
      </Box>
    </Box>
  )
}

// Standard's `VideoCallItem` card content.
const VideoCallBody = ({ facts }: { facts: VideoCallFacts }) => {
  const { cancelledAt, cancelledBy, coachID, startTime } = facts
  const withCoach = (
    <>
      Call with{' '}
      <strong>
        <CoachName coachID={coachID} />
      </strong>
    </>
  )

  if (cancelledAt) {
    const by = cancelledBy === VideoCallCancelledBy.Participant ? 'by the participant' : 'by the care team'
    return (
      <Typography variant="body2">
        {withCoach} was cancelled on {formatEventTime(cancelledAt)}
        {cancelledBy && ` ${by}`}
      </Typography>
    )
  }

  return (
    <Typography variant="body2">
      {withCoach} {callHasEnded(facts) ? 'was had on' : 'is scheduled for'} {formatEventTime(startTime)}
    </Typography>
  )
}

const ActivityFactsBody = ({ facts }: { facts: ActivityFacts }) => {
  switch (facts.kind) {
    case 'labOrder':
      return <LabOrderBody facts={facts} />
    case 'videoCall':
      return <VideoCallBody facts={facts} />
    case 'overdueQuestionnaire':
      return null
    default: {
      const unhandled: never = facts
      return unhandled
    }
  }
}

export default ActivityFactsBody
